export const AUTH_SECRET_PLACEHOLDER = 'shipany-dev-secret-change-in-production';

// Isomorphic env access:
// - Public (client-visible) vars are VITE_-prefixed and read from
//   import.meta.env (statically injected into the client bundle by Vite).
// - Server-only vars (secrets) are read from process.env and resolve to ''
//   in the browser — they never reach the client bundle.
const metaEnv: Record<string, string | undefined> =
  (import.meta as any).env ?? {};
const procEnv: Record<string, string | undefined> =
  typeof process !== 'undefined' && process.env ? process.env : {};

const getWorkerEnv = (): Record<string, string | undefined> =>
  typeof globalThis !== 'undefined' ? ((globalThis as any).__CF_ENV__ ?? {}) : {};

const serverEnv = (key: string) => getWorkerEnv()[key] ?? procEnv[key];
const publicEnv = (key: string) => getWorkerEnv()[key] ?? metaEnv[key] ?? procEnv[key];

export const canonicalSiteUrl = 'https://doqcreative.com';

const staticEnvConfigs: Record<string, string> = {
  // App (public)
  app_url: publicEnv('VITE_APP_URL') ?? canonicalSiteUrl,
  app_name: publicEnv('VITE_APP_NAME') ?? '渡橋',
  app_description: publicEnv('VITE_APP_DESCRIPTION') ?? '香港網頁設計、SEO 搜尋優化、Google Ads 與 Meta 廣告支援。',
  app_logo: publicEnv('VITE_APP_LOGO') ?? '/images/doq-logo.webp',

  // Database
  database_url: serverEnv('DATABASE_URL') ?? '',
  database_auth_token: serverEnv('DATABASE_AUTH_TOKEN') ?? '',
  database_provider: serverEnv('DATABASE_PROVIDER') ?? 'sqlite',
  db_schema: serverEnv('DB_SCHEMA') ?? 'public',
  db_singleton_enabled: serverEnv('DB_SINGLETON_ENABLED') ?? 'false',
  db_max_connections: serverEnv('DB_MAX_CONNECTIONS') ?? '1',

  // Auth
  auth_url: serverEnv('AUTH_URL') ?? publicEnv('VITE_APP_URL') ?? '',
  auth_secret: serverEnv('AUTH_SECRET') ?? '',

  // Payment - Stripe
  stripe_secret_key: serverEnv('STRIPE_SECRET_KEY') ?? '',
  stripe_publishable_key: serverEnv('STRIPE_PUBLISHABLE_KEY') ?? '',
  stripe_signing_secret: serverEnv('STRIPE_SIGNING_SECRET') ?? '',

  // Payment - PayPal
  paypal_client_id: serverEnv('PAYPAL_CLIENT_ID') ?? '',
  paypal_client_secret: serverEnv('PAYPAL_CLIENT_SECRET') ?? '',
  paypal_webhook_id: serverEnv('PAYPAL_WEBHOOK_ID') ?? '',
  paypal_environment: serverEnv('PAYPAL_ENVIRONMENT') ?? 'production',

  // Payment - Alipay
  alipay_app_id: serverEnv('ALIPAY_APP_ID') ?? '',
  alipay_private_key: serverEnv('ALIPAY_PRIVATE_KEY') ?? '',
  alipay_public_key: serverEnv('ALIPAY_PUBLIC_KEY') ?? '',
  alipay_notify_url: serverEnv('ALIPAY_NOTIFY_URL') ?? '',

  // Payment - WeChat Pay
  wechat_app_id: serverEnv('WECHAT_APP_ID') ?? '',
  wechat_mch_id: serverEnv('WECHAT_MCH_ID') ?? '',
  wechat_api_v3_key: serverEnv('WECHAT_API_V3_KEY') ?? '',
  wechat_private_key: serverEnv('WECHAT_PRIVATE_KEY') ?? '',
  wechat_serial_no: serverEnv('WECHAT_SERIAL_NO') ?? '',
  wechat_notify_url: serverEnv('WECHAT_NOTIFY_URL') ?? '',
  wechat_platform_cert: serverEnv('WECHAT_PLATFORM_CERT') ?? '',

  // Email - Resend
  resend_api_key: serverEnv('RESEND_API_KEY') ?? '',
  resend_sender_email: serverEnv('RESEND_SENDER_EMAIL') ?? serverEnv('RESEND_EMAIL_FROM') ?? '',

  // Storage - S3/R2
  storage_endpoint: serverEnv('STORAGE_ENDPOINT') ?? '',
  storage_region: serverEnv('STORAGE_REGION') ?? 'auto',
  storage_access_key: serverEnv('STORAGE_ACCESS_KEY') ?? '',
  storage_secret_key: serverEnv('STORAGE_SECRET_KEY') ?? '',
  storage_bucket: serverEnv('STORAGE_BUCKET') ?? '',
  storage_public_domain: serverEnv('STORAGE_PUBLIC_DOMAIN') ?? '',
  inline_image_max_kb: serverEnv('INLINE_IMAGE_MAX_KB') ?? '2048',

  // AI
  // OpenAI / Anthropic are admin-panel-only (like Gemini/Fal). No env fallback:
  // OPENAI_API_KEY / ANTHROPIC_API_KEY are common ambient vars, and falling back
  // to them would let the admin "Test" silently pass on the machine's own key.
  replicate_api_token: serverEnv('REPLICATE_API_TOKEN') ?? '',

  // WhatsApp lead attribution
  google_sheets_webhook_url: serverEnv('GOOGLE_SHEETS_WEBHOOK_URL') ?? '',
  google_sheets_webhook_secret: serverEnv('GOOGLE_SHEETS_WEBHOOK_SECRET') ?? '',
  analytics_admin_password_hash: serverEnv('ANALYTICS_ADMIN_PASSWORD_HASH') ?? '',

  // Locale (public)
  locale: publicEnv('VITE_DEFAULT_LOCALE') ?? 'en',
};

const dynamicEnvKeys: Record<string, () => string | undefined> = {
  app_url: () => publicEnv('VITE_APP_URL'),
  app_name: () => publicEnv('VITE_APP_NAME'),
  app_description: () => publicEnv('VITE_APP_DESCRIPTION'),
  app_logo: () => publicEnv('VITE_APP_LOGO'),
  database_url: () => serverEnv('DATABASE_URL'),
  database_auth_token: () => serverEnv('DATABASE_AUTH_TOKEN'),
  database_provider: () => serverEnv('DATABASE_PROVIDER'),
  db_schema: () => serverEnv('DB_SCHEMA'),
  db_singleton_enabled: () => serverEnv('DB_SINGLETON_ENABLED'),
  db_max_connections: () => serverEnv('DB_MAX_CONNECTIONS'),
  auth_url: () => serverEnv('AUTH_URL') ?? publicEnv('VITE_APP_URL'),
  auth_secret: () => serverEnv('AUTH_SECRET'),
  stripe_secret_key: () => serverEnv('STRIPE_SECRET_KEY'),
  stripe_publishable_key: () => serverEnv('STRIPE_PUBLISHABLE_KEY'),
  stripe_signing_secret: () => serverEnv('STRIPE_SIGNING_SECRET'),
  paypal_client_id: () => serverEnv('PAYPAL_CLIENT_ID'),
  paypal_client_secret: () => serverEnv('PAYPAL_CLIENT_SECRET'),
  paypal_webhook_id: () => serverEnv('PAYPAL_WEBHOOK_ID'),
  paypal_environment: () => serverEnv('PAYPAL_ENVIRONMENT'),
  alipay_app_id: () => serverEnv('ALIPAY_APP_ID'),
  alipay_private_key: () => serverEnv('ALIPAY_PRIVATE_KEY'),
  alipay_public_key: () => serverEnv('ALIPAY_PUBLIC_KEY'),
  alipay_notify_url: () => serverEnv('ALIPAY_NOTIFY_URL'),
  wechat_app_id: () => serverEnv('WECHAT_APP_ID'),
  wechat_mch_id: () => serverEnv('WECHAT_MCH_ID'),
  wechat_api_v3_key: () => serverEnv('WECHAT_API_V3_KEY'),
  wechat_private_key: () => serverEnv('WECHAT_PRIVATE_KEY'),
  wechat_serial_no: () => serverEnv('WECHAT_SERIAL_NO'),
  wechat_notify_url: () => serverEnv('WECHAT_NOTIFY_URL'),
  wechat_platform_cert: () => serverEnv('WECHAT_PLATFORM_CERT'),
  resend_api_key: () => serverEnv('RESEND_API_KEY'),
  resend_sender_email: () => serverEnv('RESEND_SENDER_EMAIL') ?? serverEnv('RESEND_EMAIL_FROM'),
  storage_endpoint: () => serverEnv('STORAGE_ENDPOINT'),
  storage_region: () => serverEnv('STORAGE_REGION'),
  storage_access_key: () => serverEnv('STORAGE_ACCESS_KEY'),
  storage_secret_key: () => serverEnv('STORAGE_SECRET_KEY'),
  storage_bucket: () => serverEnv('STORAGE_BUCKET'),
  storage_public_domain: () => serverEnv('STORAGE_PUBLIC_DOMAIN'),
  inline_image_max_kb: () => serverEnv('INLINE_IMAGE_MAX_KB'),
  replicate_api_token: () => serverEnv('REPLICATE_API_TOKEN'),
  google_sheets_webhook_url: () => serverEnv('GOOGLE_SHEETS_WEBHOOK_URL'),
  google_sheets_webhook_secret: () => serverEnv('GOOGLE_SHEETS_WEBHOOK_SECRET'),
  analytics_admin_password_hash: () => serverEnv('ANALYTICS_ADMIN_PASSWORD_HASH'),
  locale: () => publicEnv('VITE_DEFAULT_LOCALE'),
};

export const envConfigs: Record<string, string> = new Proxy(staticEnvConfigs, {
  get(target, prop, receiver) {
    if (typeof prop !== 'string') return Reflect.get(target, prop, receiver);
    const value = dynamicEnvKeys[prop]?.();
    return value ?? Reflect.get(target, prop, receiver);
  },
});
